import { useState } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Card,
    CardHeader,
    IconButton,
    Tooltip,
    Chip,
    TablePagination,
    TextField,
    InputAdornment,
    Stack,
    TableSortLabel,
    Paper
} from '@mui/material';
import { 
    Refresh as RefreshIcon,
    Search as SearchIcon,
    Clear as ClearIcon,
    OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { DateTime } from 'luxon';

const AdminChildren = () => {
    const [children, setChildren] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'lastName', direction: 'asc' });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [totalCount, setTotalCount] = useState(0);
    const [hasSearched, setHasSearched] = useState(false);
    const navigate = useNavigate();

    const fetchChildren = async (pageNum = page + 1, limit = rowsPerPage) => {
        if (!searchQuery.trim()) {
            setChildren([]);
            setTotalCount(0);
            setHasSearched(false);
            return;
        }

        try {
            setLoading(true);
            const response = await api.get('/admin/children/search', {
                params: {
                    query: searchQuery,
                    page: pageNum,
                    limit
                }
            });
            setChildren(response.data.children || []);
            setTotalCount(response.data.pagination?.total || 0);
            setHasSearched(true);
        } catch (error) {
            setError('Failed to load children');
        } finally {
            setLoading(false);
        }
    };

    const handleRequestSort = (property) => {
        const isAsc = sortConfig.key === property && sortConfig.direction === 'asc';
        setSortConfig({ key: property, direction: isAsc ? 'desc' : 'asc' });
    };

    const sortData = (data) => {
        return data.sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];

            if (aValue === bValue) {
                return 0;
            }
            
            const comparison = aValue < bValue ? -1 : 1;
            return sortConfig.direction === 'desc' ? -comparison : comparison;
        });
    };

    const handleSearch = (e) => {
        e.preventDefault();
        if (!searchQuery.trim()) return;
        setPage(0);
        fetchChildren(1, rowsPerPage);
    };

    const handleClearSearch = () => {
        setSearchQuery('');
        setChildren([]);
        setTotalCount(0);
        setPage(0);
        setHasSearched(false);
        setError(null);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
        fetchChildren(newPage + 1, rowsPerPage);
    };

    const handleChangeRowsPerPage = (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(newRowsPerPage);
        setPage(0);
        fetchChildren(1, newRowsPerPage);
    };

    // Get sorted children
    const displayedChildren = sortData([...children]);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return DateTime.fromISO(dateString).toLocaleString(DateTime.DATE_SHORT);
    };

    const SortableTableCell = ({ id, label, numeric = false }) => (
        <TableCell
            sortDirection={sortConfig.key === id ? sortConfig.direction : false}
            align={numeric ? 'right' : 'left'}
        >
            <TableSortLabel
                active={sortConfig.key === id}
                direction={sortConfig.key === id ? sortConfig.direction : 'asc'}
                onClick={() => handleRequestSort(id)}
            >
                {label}
            </TableSortLabel>
        </TableCell>
    );

    const handleViewParent = (parentInfo) => {
        if (parentInfo?.id) {
            navigate(`/admin/clients/${parentInfo.id}`);
        }
    };

    return (
        <Box>
            <Typography variant="h4" gutterBottom>
                Children
            </Typography>

            <Card sx={{ mb: 4 }}>
                <CardHeader
                    title="Child Search"
                    action={
                        <Stack direction="row" spacing={2}>
                            <Tooltip title="Refresh results">
                                <IconButton 
                                    onClick={() => fetchChildren(page + 1, rowsPerPage)} 
                                    disabled={loading || !searchQuery.trim()}
                                >
                                    <RefreshIcon />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    }
                />
                <Box sx={{ px: 3, pb: 3 }}>
                    <form onSubmit={handleSearch}>
                        <TextField
                            fullWidth
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search by name..."
                            required
                            error={hasSearched && !searchQuery.trim()}
                            helperText={hasSearched && !searchQuery.trim() ? "Search query is required" : ""}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: searchQuery && (
                                    <InputAdornment position="end">
                                        <IconButton onClick={handleClearSearch} edge="end">
                                            <ClearIcon />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </form>
                </Box>
            </Card>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            <Paper>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <SortableTableCell id="lastName" label="Name" />
                                <SortableTableCell id="dateOfBirth" label="Date of Birth" />
                                <SortableTableCell id="gender" label="Gender" />
                                <TableCell>Parent</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : !hasSearched ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">
                                        Enter a search query to find children
                                    </TableCell>
                                </TableRow>
                            ) : displayedChildren.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">
                                        No children found
                                    </TableCell>
                                </TableRow>
                            ) : (
                                displayedChildren.map((child) => (
                                    <TableRow
                                        key={child.id}
                                        hover
                                        sx={{ cursor: child.isClaimed ? 'pointer' : 'default' }}
                                    >
                                        <TableCell>
                                            {child.firstName} {child.lastName}
                                        </TableCell>
                                        <TableCell>{formatDate(child.dateOfBirth)}</TableCell>
                                        <TableCell>{child.gender}</TableCell>
                                        <TableCell>
                                            {child.isClaimed ? (
                                                <Chip
                                                    label={`Claimed by ${child.parentInfo?.name}`}
                                                    color="primary"
                                                    size="small"
                                                    onClick={() => handleViewParent(child.parentInfo)}
                                                    icon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                                                    sx={{ 
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            bgcolor: 'primary.dark',
                                                        }
                                                    }}
                                                />
                                            ) : (
                                                <Chip
                                                    label="Unclaimed"
                                                    color="default"
                                                    size="small"
                                                />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                {hasSearched && (
                    <TablePagination
                        component="div"
                        count={totalCount}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                )}
            </Paper>
        </Box>
    );
};

export default AdminChildren; 